<template>
  <div id="keyword-intro">
    <!-- 히어로 + 검색 진입 -->
    <section class="hero">
      <div class="hero-badge">블로그 글감 찾기</div>
      <h1 class="hero-title">
        이 키워드로 글을 쓰면<br />
        <em>사람이 들어올까?</em>
      </h1>
      <p class="hero-desc">
        검색량이 많아도 이미 글이 넘치면 묻히고, 경쟁이 없어도 아무도 안 찾으면 의미가 없습니다.<br />
        네이버 데이터로 <b>글을 쓰기 전에</b> 그 답을 숫자로 확인하세요.
      </p>

      <!-- 로그인 여부와 무관하게 항상 보인다. 동작만 달라진다. -->
      <div class="hero-search">
        <input
          v-model="seed"
          class="hero-input"
          type="text"
          placeholder="예: 여의도맛집"
          maxlength="100"
          @keyup.enter="goSearch"
        />
        <button class="hero-btn" @click="goSearch">키워드 찾기</button>
      </div>
      <p class="hero-note">
        <template v-if="isLoggedIn">관심 주제를 아무 단어나 넣어 보세요.</template>
        <template v-else>로그인이 필요한 서비스입니다. 검색하면 로그인 화면으로 안내합니다.</template>
      </p>
    </section>

    <!-- 결과 화면 미리보기 (실제 화면을 축소해 흉내낸 정적 예시) -->
    <section class="section">
      <h2 class="section-title">이런 화면이 나옵니다</h2>
      <div class="preview">
        <div class="pv-chrome">
          <span class="pv-dot"></span><span class="pv-dot"></span><span class="pv-dot"></span>
          <span class="pv-url">breezer.co.kr/keyword/search?q=여의도맛집</span>
        </div>

        <div class="pv-body">
          <div class="pv-tabs">
            <span class="pv-tab active">기본정보</span>
            <span class="pv-tab">트렌드</span>
            <span class="pv-tab">성향</span>
            <span class="pv-tab">연관 키워드 <em>40</em></span>
            <span class="pv-tab">경쟁 글</span>
          </div>

          <div class="pv-verdict">
            <div class="pv-vt">해볼 만합니다</div>
            <div class="pv-vd">검색 대비 글이 아주 많지는 않습니다. 아래 연관 키워드에서 더 좁은 글감을 골라 보세요.</div>
          </div>

          <div class="pv-metrics">
            <div class="pv-m"><span>월간 검색량</span><b>76,500</b><em>중형 키워드</em></div>
            <div class="pv-m"><span>월간 발행량</span><b>144,013</b><em>블로그 문서 수</em></div>
            <div class="pv-m"><span>포화 지수</span><b class="mid">1.88</b><em>보통</em></div>
            <div class="pv-m"><span>1위 입찰가</span><b>4,780원</b><em>상업적 가치 큼</em></div>
          </div>

          <div class="pv-table">
            <div class="pv-tr pv-th"><span>키워드</span><span>검색량</span><span>발행량</span><span>포화도</span></div>
            <div class="pv-tr"><span>여의도점심</span><span>1,880</span><span>1,240</span><span class="good">0.66</span></div>
            <div class="pv-tr"><span>여의도횟집</span><span>2,750</span><span>2,390</span><span class="good">0.87</span></div>
            <div class="pv-tr"><span>여의도맛집</span><span>76,500</span><span>144,013</span><span class="mid">1.88</span></div>
            <div class="pv-tr"><span>여의도이자카야</span><span>3,330</span><span>9,100</span><span class="mid">2.73</span></div>
          </div>
          <div class="pv-caption">
            포화도가 낮은 순으로 정렬됩니다 — 큰 키워드보다 <b>작지만 먹을 수 있는</b> 키워드가 위로 올라옵니다.
          </div>
        </div>
      </div>
      <p class="preview-note">
        실제 화면을 축소한 예시입니다. 탭을 눌러 트렌드(1년 추이)·성향(요일·성별·연령)·연관 키워드를 오갈 수 있습니다.
      </p>
    </section>

    <!-- 핵심 지표 3개 -->
    <section class="section">
      <h2 class="section-title">세 개의 숫자로 판단합니다</h2>
      <div class="metric-cards">
        <div class="metric-card">
          <div class="mc-icon">🔍</div>
          <div class="mc-name">월간 검색량</div>
          <div class="mc-desc">최근 한 달 동안 이 키워드가 검색된 횟수. PC와 모바일을 나눠서 봅니다.</div>
          <div class="mc-tip">모바일 비중이 높으면 짧은 문단과 세로 사진이 유리합니다.</div>
        </div>
        <div class="metric-card">
          <div class="mc-icon">📄</div>
          <div class="mc-name">월간 발행량</div>
          <div class="mc-desc">같은 기간 이 키워드로 발행된 블로그 글의 수. 많을수록 경쟁이 치열합니다.</div>
          <div class="mc-tip">검색량만 보고 뛰어들면 이미 포화된 곳에 들어가게 됩니다.</div>
        </div>
        <div class="metric-card highlight">
          <div class="mc-icon">⚖️</div>
          <div class="mc-name">포화 지수</div>
          <div class="mc-desc">발행량 ÷ 검색량. <b>낮을수록</b> 경쟁 대비 노출에 유리합니다.</div>
          <div class="mc-tip">이 서비스가 기본으로 <b>포화도 낮은 순</b>으로 정렬하는 이유입니다.</div>
        </div>
      </div>
    </section>

    <!-- 사용 흐름 -->
    <section class="section">
      <h2 class="section-title">쓰는 방법</h2>
      <ol class="flow">
        <li>
          <span class="flow-num">1</span>
          <div>
            <b>씨앗 키워드를 하나 넣습니다</b>
            <p>예: <code>여의도맛집</code>. 블로그 주제와 관련된 아무 단어면 됩니다.</p>
          </div>
        </li>
        <li>
          <span class="flow-num">2</span>
          <div>
            <b>연관 키워드가 1,000개 안팎 펼쳐집니다</b>
            <p>네이버 검색광고 데이터 기준입니다. 직접 떠올리기 어려운 조합까지 나옵니다.</p>
          </div>
        </li>
        <li>
          <span class="flow-num">3</span>
          <div>
            <b>여섯 개 탭으로 나눠 봅니다</b>
            <p>
              <code>기본정보</code> 검색량·포화도·입찰가와 종합 판정 ·
              <code>트렌드</code> 1년 추이와 성수기, <b>급등 감지</b> ·
              <code>성향</code> 요일별·성별·연령대 ·
              <code>연관 키워드</code> 표와 CSV ·
              <code>경쟁 글</code> 최근 발행된 글 ·
              <code>지표 읽는 법</code>
            </p>
          </div>
        </li>
        <li>
          <span class="flow-num">4</span>
          <div>
            <b>포화도가 낮은 키워드를 골라 씁니다</b>
            <p>표에서 키워드를 누르면 그 키워드로 바로 다시 분석됩니다. 발행 요일과 시기까지 정할 수 있습니다.</p>
          </div>
        </li>
      </ol>
      <p class="flow-note">
        한 번 찾은 키워드는 <b>최근 10건까지 남습니다.</b> 다시 누르면 네이버를 부르지 않고
        저장해 둔 결과를 그대로 보여 줍니다 — 다른 메뉴에 다녀와도 화면이 비지 않습니다.
      </p>
    </section>

    <!-- 네 개의 화면 -->
    <section class="section">
      <h2 class="section-title">세 가지 화면이 있습니다</h2>
      <div class="menu-cards">
        <router-link to="/keyword/popular" class="menu-card">
          <div class="mn-name">인기 키워드</div>
          <p>주제(맛집·여행·육아 …)를 고르면 그 분야에서 많이 검색되는 말을 순위로 봅니다. <b>로그인 없이</b> 바로 볼 수 있어요.</p>
        </router-link>
        <router-link to="/keyword/search" class="menu-card">
          <div class="mn-name">키워드 검색</div>
          <p>키워드 하나를 넣으면 검색량·포화도·트렌드·성향·연관 키워드를 탭으로 나눠 봅니다. (로그인 필요)</p>
        </router-link>
        <router-link to="/keyword/title" class="menu-card">
          <div class="mn-name">제목 진단</div>
          <p>쓰려는 제목을 넣으면 <b>검색 수요·경쟁·형식을 100점</b>으로 채점하고, 더 나은 제목 후보를 만들어 줍니다. (로그인 필요)</p>
        </router-link>
      </div>
    </section>

    <!-- 왜 로그인이 필요한가 -->
    <section class="section">
      <h2 class="section-title">왜 로그인이 필요한가요?</h2>
      <div class="notice-box">
        <p>
          <b>로그인만 하면 바로 쓸 수 있습니다.</b> 따로 신청하거나 API 키를 발급받지 않아도 됩니다.
        </p>
        <p>
          로그인을 받는 이유는 두 가지입니다.
          네이버 API는 <b>호출 수에 한도</b>가 있어 누가 얼마나 쓰는지 확인해야 하고,
          조회 결과를 <b>내 이력으로 다시 볼 수 있게</b> 하기 위해서입니다.
        </p>
        <p class="notice-sub">
          호출이 몰리면 잠시 닫아둘 수 있습니다. 그때는 화면에 안내가 뜹니다.
          <b>인기 키워드</b>는 로그인 없이도 볼 수 있습니다.
        </p>
      </div>
    </section>

    <!-- 자주 묻는 것 -->
    <section class="section">
      <h2 class="section-title">미리 알아두면 좋은 것</h2>
      <dl class="faq">
        <dt>실시간 인기 검색어는 볼 수 없나요?</dt>
        <dd>
          네이버가 2021년에 실시간 급상승 검색어 서비스를 폐지했고, 인기 검색어 목록을 주는 API도 없습니다.
          대신 관심 주제를 씨앗으로 넣어 연관 키워드를 펼치는 방식으로 글감을 찾습니다.
        </dd>
        <dt>상위 노출 순위를 확인할 수 있나요?</dt>
        <dd>
          검색 결과 순위를 제공하는 공식 API가 없습니다. 크롤링으로 우회하지 않는다는 원칙이라 제공하지 않습니다.
        </dd>
        <dt>조회에 시간이 걸리나요?</dt>
        <dd>
          한 번에 15~25초 걸립니다. 발행량은 네이버 호출 제한 때문에 <b>한 건씩 순차로</b> 조회해야 합니다.
          한 번 찾은 키워드는 저장해 두므로 다시 볼 때는 기다리지 않습니다.
        </dd>
        <dt>연관 키워드가 왜 엉뚱한 게 섞이나요?</dt>
        <dd>
          검색광고의 연관 키워드는 뜻이 비슷한 말이 아니라 <b>같은 광고주가 함께 사는 키워드</b>입니다.
          그래서 「라이코펜」에 「델몬트」 같은 것이 섞입니다.
          표 위의 <b>씨앗 포함</b>을 누르면 입력한 말을 품은 것만 볼 수 있습니다.
        </dd>
        <dt>제목 점수는 무엇을 기준으로 하나요?</dt>
        <dd>
          제목이 담고 있는 낱말 중 <b>실제로 이길 수 있는 말</b> 하나를 골라 그 기준으로 잽니다.
          검색량이 아무리 커도 이미 글이 넘치는 말(행정구역명·범용어)은 제외하고,
          왜 뺐는지 화면에 적어 둡니다.
        </dd>
      </dl>
    </section>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import * as core from "@/common/utils/core";
import * as routerUtil from "@/common/utils/routerUtil";

export default defineComponent({
  name: "KeywordHomeView",
  data() {
    return { seed: "" };
  },
  computed: {
    isLoggedIn(): boolean {
      const info = core.getLoginInfo();
      return !!(info && info.id);
    },
  },
  methods: {
    /**
     * 홈에서 키워드를 입력하면 검색 화면으로 넘긴다.
     * 비로그인이면 로그인 화면으로 보내되, nextRoute 에 검색 경로+키워드를 실어
     * 로그인 직후 바로 그 키워드로 조회되게 한다.
     */
    goSearch() {
      const seed = this.seed.trim();
      if (!seed) {
        return;
      }
      const target = { path: "/keyword/search", query: { q: seed } };

      if (this.isLoggedIn) {
        this.$router.push(target);
        return;
      }

      alert("로그인이 필요한 서비스입니다. 로그인 후 입력하신 키워드로 바로 조회됩니다.");
      const nextRoute = encodeURIComponent(JSON.stringify(target));
      routerUtil.moveTo("/user/login", nextRoute);
    },
  },
});
</script>

<style lang="scss" scoped></style>
